import { type StageCue, TITLE_PREFIX_RE, TONE_TOKENS, titlePrefix } from './stage';

/** Composites the stage dot onto the app icon; returns a data: URL, or null. */
export type FaviconRenderer = (color: string, ring: string) => Promise<string | null>;

const ORIG_HREF = 'data-lvdr-orig-href';
const ORIG_TYPE = 'data-lvdr-orig-type';
const ORIG_CONTENT = 'data-lvdr-orig-content';

interface Applied {
  cue: StageCue;
  renderFavicon: FaviconRenderer;
  /** Resolved token colours, kept so a theme change can tell whether they moved. */
  fill: string | null;
  mark: string | null;
  ring: string | null;
  favicon: string | null;
}

let current: Applied | null = null;
let headObserver: MutationObserver | null = null;
let themeObserver: MutationObserver | null = null;
// Bumped by every apply/clear, so a favicon render that resolves after the cue
// changed (or was cleared) is dropped rather than painted over the new state.
let generation = 0;

/** A palette token as a CSS colour, or null when the stylesheet has not defined it. */
export function tokenColor(token: string): string | null {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
  return v ? `hsl(${v.split(/\s+/).join(', ')})` : null;
}

// `~=` matches the whole rel token: icon and "shortcut icon", never apple-touch-icon.
const iconLinks = () => document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');
const themeMetas = () => document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');

function reapply(): void {
  if (!current) return;
  // Compare with what document.title reads BACK, which has its whitespace
  // stripped: on an empty <title>, '[PRE] ' reads as '[PRE]', and a check for
  // the full prefix would stack a second one on the next head mutation.
  const prefix = titlePrefix(current.cue);
  const rest = document.title.replace(TITLE_PREFIX_RE, '');
  const want = rest ? prefix + rest : prefix.trimEnd();
  if (document.title !== want) document.title = want;
  if (current.fill) {
    for (const m of themeMetas()) {
      if (!m.hasAttribute(ORIG_CONTENT)) m.setAttribute(ORIG_CONTENT, m.content);
      if (m.content !== current.fill) m.content = current.fill;
    }
  }
  if (current.favicon) {
    for (const l of iconLinks()) {
      if (!l.hasAttribute(ORIG_HREF)) {
        l.setAttribute(ORIG_HREF, l.getAttribute('href') ?? '');
        const type = l.getAttribute('type');
        if (type !== null) l.setAttribute(ORIG_TYPE, type);
      }
      if (l.getAttribute('href') !== current.favicon) l.setAttribute('href', current.favicon);
      if (l.getAttribute('type') !== 'image/png') l.setAttribute('type', 'image/png');
    }
  }
}

/**
 * The app theme flips `.dark` on <html> (useApplyTheme) without touching the
 * stage store. The stripe and the chip follow through CSS variables; the
 * theme-colour and the favicon dot hold resolved colours, so re-resolve them.
 * Re-apply only when a colour actually moved: useApplyTheme re-writes the same
 * class on every mount, which would otherwise redraw the favicon each time.
 */
function onThemeChange(): void {
  if (!current) return;
  const { cue, renderFavicon, fill, mark, ring } = current;
  const t = TONE_TOKENS[cue.tone];
  const unchanged =
    tokenColor(t.fill) === fill && tokenColor(t.mark) === mark && tokenColor('background') === ring;
  if (!unchanged) applyStageMarker(cue, renderFavicon);
}

/**
 * Resolves once the page has loaded and the main thread has then gone idle,
 * i.e. after React has finished hydrating, Next's metadata included. The hint
 * path (StageMarker, before the server answers) waits for it before it touches
 * <link rel="icon">: React 19 finds a server-rendered hoisted link by its href
 * while hydrating, so a link already pointed at the data: URL is not found and
 * React inserts a duplicate. Measured in a real browser (verify-goal-cues L1):
 * 8 icon links on every reload with a hint, and 4 when the favicon landed
 * after hydration. Safari has no requestIdleCallback; a short timeout after
 * `load` stands in.
 */
export function afterHydration(): Promise<void> {
  return new Promise((resolve) => {
    const idle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 1000 });
      } else {
        setTimeout(resolve, 50);
      }
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  });
}

/**
 * Applies every stage cue for `cue` and keeps them applied: a head observer
 * re-runs reapply() when Next rewrites <title> on navigation or restores an
 * icon/meta attribute, and a class observer on <html> follows a theme toggle.
 * reapply() writes only on a difference, so its own writes settle in one pass
 * instead of looping.
 *
 * `faviconGate`, when given, holds the favicon render (and so every icon-link
 * write) until it resolves; title, tone and theme-colour apply at once. A
 * render whose apply was superseded while it waited is skipped, not run.
 */
export function applyStageMarker(
  cue: StageCue,
  renderFavicon: FaviconRenderer = renderStageFavicon,
  faviconGate?: Promise<unknown>,
): void {
  const gen = ++generation;
  const t = TONE_TOKENS[cue.tone];
  const mark = tokenColor(t.mark);
  const ring = tokenColor('background');
  current = { cue, renderFavicon, fill: tokenColor(t.fill), mark, ring, favicon: null };
  const d = document.documentElement;
  // Write only on a difference: the pre-hydration script has usually set it
  // already, and StageMarker's no-flicker test asserts no mutation on it.
  if (d.getAttribute('data-lvdr-tone') !== cue.tone) d.setAttribute('data-lvdr-tone', cue.tone);
  reapply();
  if (!headObserver) {
    headObserver = new MutationObserver(reapply);
    headObserver.observe(document.head, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['href', 'type', 'content'],
    });
  }
  if (!themeObserver) {
    themeObserver = new MutationObserver(onThemeChange);
    themeObserver.observe(d, { attributes: true, attributeFilter: ['class'] });
  }
  if (!mark || !ring) return;
  const paint = () =>
    renderFavicon(mark, ring)
      .then((url) => {
        if (gen !== generation || !current || !url) return;
        current.favicon = url;
        reapply();
      })
      .catch(() => {});
  if (!faviconGate) {
    void paint();
    return;
  }
  faviconGate
    .then(() => {
      if (gen === generation) void paint();
    })
    .catch(() => {});
}

/** Removes every cue and restores the originals it saved. */
export function clearStageMarker(): void {
  generation++;
  current = null;
  headObserver?.disconnect();
  headObserver = null;
  themeObserver?.disconnect();
  themeObserver = null;
  document.documentElement.removeAttribute('data-lvdr-tone');
  // Write only when a prefix is there: every prod page load reaches this, and
  // an unconditional write replaces the <title> text node for nothing.
  const stripped = document.title.replace(TITLE_PREFIX_RE, '');
  if (stripped !== document.title) document.title = stripped;
  for (const l of iconLinks()) {
    if (!l.hasAttribute(ORIG_HREF)) continue;
    l.setAttribute('href', l.getAttribute(ORIG_HREF) ?? '');
    const type = l.getAttribute(ORIG_TYPE);
    if (type === null) l.removeAttribute('type');
    else l.setAttribute('type', type);
    l.removeAttribute(ORIG_HREF);
    l.removeAttribute(ORIG_TYPE);
  }
  for (const m of themeMetas()) {
    if (!m.hasAttribute(ORIG_CONTENT)) continue;
    m.content = m.getAttribute(ORIG_CONTENT) ?? '';
    m.removeAttribute(ORIG_CONTENT);
  }
}

/** Browser-only: draws /icon-192.png at 64px with the stage dot bottom-right. */
export async function renderStageFavicon(color: string, ring: string): Promise<string | null> {
  try {
    const img = new Image();
    img.src = '/icon-192.png';
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, 64, 64);
    ctx.beginPath();
    ctx.arc(48, 48, 15, 0, 2 * Math.PI);
    ctx.fillStyle = ring;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(48, 48, 11, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
